import { useTracking } from 'react-tracking';

import loadable from '@loadable/component';

import { useMount } from '@simplywallst/ui-core';

import { OneColumn } from '@/components/Layout';
import DocumentHeader from '@/components/DocumentHeader';

import { useFlag } from '@/features/unleash';

import { CompanyNoteModalProvider } from '@/components/Note';
import { ManualPortfolioProvider } from '../../components/PortfolioManual';

import { useDispatch } from 'react-redux';
import { redirect } from 'redux-first-router';

import { ROUTE_MY_PORTFOLIOS } from '@/constants/routes';

import { useDemoPortfolio } from '@/hooks/portfolio/useDemoPortfolio';

const ErrorState = loadable(() => import('../../components/ErrorState'), {
  resolveComponent: (c) => c.ErrorState,
});

const PopulatedState = loadable(() => import('./components/PopulatedState'), {
  resolveComponent: (c) => c.PopulatedState,
});

const getDemoPortfolioId = () => {
  if (SWS_ENV === 'prod' || SWS_ENV === 'staging') {
    return '2b0a7e79-725b-4f37-bb14-0fe197dc082f';
  }
  return '553bf742-0572-4e71-b745-b7812172d107';
};

const DemoPortfolio = () => {
  const { status, data: portfolio } = useDemoPortfolio({
    id: getDemoPortfolioId(),
  });

  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'page',
  });

  const holdings = portfolio?.holdings ?? [];

  const isEmpty = status === 'success' && portfolio && holdings.length === 0;
  const isError =
    status === 'error' || (status === 'success' && !portfolio) || isEmpty;
  const isPopulated = status !== 'error' && portfolio && holdings.length > 0;

  useMount(() => {
    trackEvent({ action: 'landed' });
  });

  if (isError) {
    return (
      <Track>
        <OneColumn>
          <ErrorState />
        </OneColumn>
      </Track>
    );
  }

  if (isPopulated) {
    return (
      <Track>
        <OneColumn>
          <ManualPortfolioProvider>
            <CompanyNoteModalProvider>
              <PopulatedState portfolio={portfolio} />
            </CompanyNoteModalProvider>
          </ManualPortfolioProvider>
        </OneColumn>
      </Track>
    );
  }

  return null;
};

const Redirect = ({ to }: { to: Parameters<typeof redirect>[0] }) => {
  const dispatch = useDispatch();

  useMount(() => {
    dispatch(redirect(to));
  });

  return null;
};

const Flag = () => {
  const flag = useFlag({ name: 'mono-portfolio-demo' });

  //  Wait for flags to be ready
  if (flag.flagsReady === false) return null;

  //  If the flag is disabled, redirect to my portfolios
  if (flag.enabled === false) {
    return (
      <Redirect
        to={{
          type: ROUTE_MY_PORTFOLIOS,
        }}
      />
    );
  }
  return <DemoPortfolio />;
};

const Root = () => {
  return (
    <>
      <DocumentHeader title="Demo Portfolio" allowCrawlers={false} />
      <Flag />
    </>
  );
};

export { Root as DemoPortfolio };
